window.theme = window.theme || {};

theme.AjaxRenderer =
  theme.AjaxRenderer ||
  (function () {
    function AjaxRenderer({ sections, onReplace, debug } = {}) {
      this.sections = sections || [];
      this.cachedSections = [];
      this.onReplace = onReplace;
      this.debug = Boolean(debug);
    }

    AjaxRenderer.prototype = Object.assign({}, AjaxRenderer.prototype, {
      renderPage: function (basePath, newParams, updateURLHash = true) {
        const currentParams = new URLSearchParams(window.location.search);
        const updatedParams = this.getUpdatedParams(currentParams, newParams);

        const sectionRenders = this.sections.map((section) => {
          const url = `${basePath}?section_id=${
            section.sectionId
          }&${updatedParams.toString()}`;
          const cachedSectionUrl = (cachedSection) => cachedSection.url === url;

          return this.cachedSections.some(cachedSectionUrl)
            ? this.renderSectionFromCache(cachedSectionUrl, section)
            : this.renderSectionFromFetch(url, section);
        });

        if (updateURLHash) this.updateURLHash(updatedParams);

        return Promise.all(sectionRenders);
      },

      renderSectionFromCache: function (url, section) {
        const cachedSection = this.cachedSections.find(url);

        this.renderSection(cachedSection.html, section);
        return Promise.resolve(section);
      },

      renderSectionFromFetch: function (url, section) {
        return new Promise((resolve, reject) => {
          fetch(url)
            .then((response) => response.text())
            .then((responseText) => {
              const html = responseText;
              this.cachedSections = [...this.cachedSections, { html, url }];
              this.renderSection(html, section);
              resolve(section);
            })
            .catch((err) => reject(err));
        });
      },

      renderSection: function (html, section) {
        const newDom = new DOMParser().parseFromString(html, 'text/html');

        if (this.onReplace) {
          this.onReplace(newDom, section);
        } else {
          var newContentEl = newDom.getElementById(section.nodeId);
          if (!newContentEl) {
            return;
          }

          document.getElementById(section.nodeId).innerHTML =
            newContentEl.innerHTML;
        }

        return section;
      },

      getUpdatedParams: function (currentParams, newParams) {
        const clone = new URLSearchParams(currentParams);
        const preservedParams = ['sort_by', 'q', 'options[prefix]', 'type'];

        // Find what params need to be removed
        // delete happens first as we cannot specify keys based off of values
        for (const [key, value] of Array.from(clone.entries())) {
          if (
            !newParams.getAll(key).includes(value) &&
            !preservedParams.includes(key)
          ) {
            clone.delete(key);
          }
        }

        for (const key of preservedParams) {
          if (newParams.has(key)) {
            clone.delete(key);
          }
        }

        // Find what params need to be added
        for (const [key, value] of newParams.entries()) {
          if (!clone.getAll(key).includes(value) && value !== '') {
            clone.append(key, value);
          }
        }

        return clone;
      },

      updateURLHash: function (searchParams) {
        history.pushState(
          {},
          '',
          `${window.location.pathname}${
            searchParams && '?'.concat(searchParams)
          }`
        );
      }
    });

    return AjaxRenderer;
  })();

theme.CollectionGrid = (function () {
  var isAnimating = false;

  var selectors = {
    sortSelect: '#SortBy',
    collectionGrid: '.collection-grid__wrapper',
    trigger: '.collapsible-trigger',
    sidebar: '#CollectionSidebar',
    activeTagList: '.tag-list--active-tags',
    tags: '.tag-list input',
    activeTags: '.tag-list a',
    tagsForm: '.filter-form',
    filters: '.collection-filter',
    priceRange: '.price-range',
    drawer: '#FilterDrawer',
    applyFilters: '.filter-btns__apply',
    clearFilters: '.filter-btns__clear'
  };

  var classes = {
    activeTag: 'tag--active',
    removeTagParent: 'tag--remove',
    filterSidebar: 'collapsible-content--sidebar',
    isOpen: 'is-open'
  };

  function CollectionGrid(container) {
    this.container = container;
    this.sectionId = container.getAttribute('data-section-id');
    this.namespace = '.collection-' + this.sectionId;
    this.ajaxRenderer = new theme.AjaxRenderer({
      sections: [{ sectionId: this.sectionId, nodeId: 'CollectionAjaxContent' }],
      onReplace: this.onReplaceAjaxContent.bind(this)
    });

    this.init();
  }

  CollectionGrid.prototype = Object.assign({}, CollectionGrid.prototype, {
    init: function () {
      this.initSort();
      this.initFilters();
      this.initBar();
      this.initDrawer();
      this.initLoadMore();
    },

    initLoadMore: function () {
      var btn = document.querySelector('.pr-load-more__btn');

      if (!btn) {
        return;
      }

      btn.addEventListener('click', this.onLoadMore.bind(this));
    },

    onLoadMore: function (evt) {
      var btn = evt.currentTarget;
      var nextUrl = btn.getAttribute('data-next-url');

      if (!nextUrl || btn.classList.contains('is-busy')) {
        return;
      }

      btn.classList.add('is-busy');
      btn.textContent = btn.getAttribute('data-loading-text') || btn.textContent;

      var joiner = nextUrl.indexOf('?') > -1 ? '&' : '?';

      fetch(nextUrl + joiner + 'section_id=' + this.sectionId)
        .then(function (response) {
          return response.text();
        })
        .then(
          function (responseText) {
            var newDom = new DOMParser().parseFromString(responseText, 'text/html');
            var grid = document.querySelector('.grid--collection');
            var newGrid = newDom.querySelector('.grid--collection');

            if (grid && newGrid) {
              while (newGrid.firstChild) {
                grid.appendChild(newGrid.firstChild);
              }
            }

            var ajaxContent = document.getElementById('CollectionAjaxContent');
            newDom.querySelectorAll('.modal--quick-shop').forEach(function (modal) {
              if (ajaxContent && !document.getElementById(modal.id)) {
                ajaxContent.appendChild(modal);
              }
            });

            var wrap = document.querySelector('.pr-load-more');
            var newWrap = newDom.querySelector('.pr-load-more');

            if (wrap) {
              if (newWrap) {
                wrap.replaceWith(newWrap);
                this.initLoadMore();
              } else {
                wrap.remove();
              }
            }

            theme.reinitProductGridItem();
            document.dispatchEvent(new CustomEvent('collection:reloaded'));
          }.bind(this)
        )
        .catch(function (err) {
          console.error('Load more failed:', err);
          btn.classList.remove('is-busy');
        });
    },

    initSort: function () {
      this.sortSelect = document.querySelector(selectors.sortSelect);

      if (!this.sortSelect) {
        return;
      }

      this.defaultSort = this.sortSelect.getAttribute('data-default-sortby');
      this.sortSelect.addEventListener('change', this.onSortChange.bind(this));
    },

    onSortChange: function () {
      if (isAnimating) {
        return;
      }

      isAnimating = true;

      var params = new URLSearchParams(window.location.search);
      params.set('sort_by', this.sortSelect.value || this.defaultSort);
      params.delete('page');

      this.updateScroll(true);
      this.startLoading();
      this.renderCollectionPage(params);
    },

    initBar: function () {
      var bar = document.querySelector('.collection-sidebar--bar');

      if (!bar) {
        return;
      }

      bar.querySelectorAll(selectors.trigger).forEach((trigger) => {
        trigger.addEventListener('click', function () {
          bar.querySelectorAll(selectors.trigger).forEach((other) => {
            if (other === trigger || !other.classList.contains(classes.isOpen)) {
              return;
            }

            other.click();
          });
        });
      });
    },

    initDrawer: function () {
      if (theme.Drawers && document.querySelector(selectors.drawer)) {
        theme.FilterDrawer = new theme.Drawers('FilterDrawer', 'collection-filters', true);
      }
    },

    /*====================
      Collection filters
    ====================*/
    initFilters: function () {
      var tags = document.querySelectorAll(selectors.tags);

      if (!tags.length) {
        return;
      }

      this.bindBackButton();

      // Set mobile top value for filters if sticky header enabled
      if (theme.config.stickyHeader) {
        this.setFilterStickyPosition();

        if (!this.resizeBound) {
          this.resizeBound = true;
          window.addEventListener('resize', this.setFilterStickyPosition.bind(this));
        }
      }

      document.querySelectorAll(selectors.activeTags).forEach((tag) => {
        tag.addEventListener('click', this.tagClick.bind(this));
      });

      document.querySelectorAll(selectors.tagsForm).forEach((form) => {
        form.addEventListener('input', this.onFormSubmit.bind(this));
      });

      var applyBtn = document.querySelector(selectors.applyFilters);
      if (applyBtn) {
        applyBtn.addEventListener('click', this.onDrawerApply.bind(this));
      }

      document.querySelectorAll(selectors.clearFilters).forEach((btn) => {
        btn.addEventListener('click', this.onClearAll.bind(this));
      });
    },

    tagClick: function (evt) {
      var el = evt.currentTarget;

      if (theme.FilterDrawer) {
        theme.FilterDrawer.close();
      }

      // Do not ajax-load collection links
      if (el.classList.contains('no-ajax')) {
        return;
      }

      evt.preventDefault();
      evt.stopPropagation();

      if (isAnimating) {
        return;
      }

      isAnimating = true;

      const parent = el.parentNode;
      const newUrl = new URL(el.href);

      this.renderActiveTag(parent, el);
      this.updateScroll(true);
      this.startLoading();
      this.renderCollectionPage(newUrl.searchParams);
    },

    onFormSubmit: function (evt) {
      var el = evt.target;

      // The drawer waits for the apply button so mobile isn't reloading on every tap
      if (el.closest(selectors.drawer)) {
        return;
      }

      if (el.type === 'number') {
        clearTimeout(this.priceTimer);
        this.priceTimer = setTimeout(
          function () {
            this.applyFilterInput(el);
          }.bind(this),
          500
        );
        return;
      }

      this.applyFilterInput(el);
    },

    applyFilterInput: function (el) {
      if (isAnimating) {
        return;
      }

      isAnimating = true;

      const parent = el.closest('li');
      const formEl = el.closest('form');
      const formData = new FormData(formEl);

      this.renderActiveTag(parent, el);
      this.updateScroll(true);
      this.startLoading();
      this.renderFromFormData(formData);
    },

    onDrawerApply: function (evt) {
      evt.preventDefault();

      var drawer = document.querySelector(selectors.drawer);
      var formEl = drawer.querySelector(selectors.tagsForm);

      if (theme.FilterDrawer) {
        theme.FilterDrawer.close();
      }

      if (!formEl || isAnimating) {
        return;
      }

      isAnimating = true;

      this.updateScroll(true);
      this.startLoading();
      this.renderFromFormData(new FormData(formEl));
    },

    onClearAll: function (evt) {
      evt.preventDefault();

      document.querySelectorAll(selectors.tagsForm).forEach((form) => {
        form.querySelectorAll('input[type="checkbox"]').forEach((input) => {
          input.checked = false;
        });
      });

      if (theme.FilterDrawer) {
        theme.FilterDrawer.close();
      }

      if (isAnimating) {
        return;
      }

      isAnimating = true;

      this.updateScroll(true);
      this.startLoading();
      this.renderCollectionPage(new URLSearchParams());
    },

    fetchOpenCollasibleFilters: function () {
      return Array.from(
        document.querySelectorAll(
          `${selectors.sidebar} ${selectors.trigger}.${classes.isOpen}`
        )
      ).map((trigger) => trigger.dataset.collapsibleId);
    },

    renderActiveTag: function (parent, el) {
      if (!parent) {
        return;
      }

      const textEl = parent.querySelector('.tag__text');

      if (parent.classList.contains(classes.activeTag)) {
        parent.classList.remove(classes.activeTag);
      } else {
        parent.classList.add(classes.activeTag);

        // If adding a tag, show new tag right away.
        // Otherwise, remove it before ajax finishes
        if (el.closest('li').classList.contains(classes.removeTagParent)) {
          parent.remove();
        } else if (textEl) {
          document.querySelectorAll(selectors.activeTagList).forEach((list) => {
            const newTag = document.createElement('li');
            const newTagLink = document.createElement('a');
            newTag.classList.add('tag', 'tag--remove');
            newTagLink.classList.add('btn', 'btn--small');
            newTagLink.innerText = textEl.innerText;
            newTag.appendChild(newTagLink);

            list.appendChild(newTag);
          });
        }
      }
    },

    renderFromFormData: function (formData) {
      const searchParams = new URLSearchParams(formData);
      this.renderCollectionPage(searchParams);
    },

    onReplaceAjaxContent: function (newDom, section) {
      const openCollapsibleIds = this.fetchOpenCollasibleFilters();

      openCollapsibleIds.forEach((selector) => {
        newDom
          .querySelectorAll(`[data-collapsible-id=${selector}]`)
          .forEach(this.openCollapsible);
      });

      var newContentEl = newDom.getElementById(section.nodeId);
      if (!newContentEl) {
        return;
      }

      document.getElementById(section.nodeId).innerHTML = newContentEl.innerHTML;
    },

    openCollapsible: function (el) {
      if (el.classList.contains(classes.filterSidebar)) {
        el.style.height = 'auto';
      }

      el.classList.add(classes.isOpen);
    },

    renderCollectionPage: function (searchParams, updateURLHash = true) {
      this.ajaxRenderer
        .renderPage(window.location.pathname, searchParams, updateURLHash)
        .then(() => {
          theme.reinitSection('collection-grid');
          this.updateScroll(false);
          theme.reinitProductGridItem();

          document.dispatchEvent(new CustomEvent('collection:reloaded'));
          isAnimating = false;
        })
        .catch((err) => {
          console.error('Filter request failed:', err);
          isAnimating = false;
        });
    },

    bindBackButton: function () {
      if (this.popstateBound) {
        return;
      }

      this.popstateBound = true;

      window.addEventListener('popstate', (state) => {
        if (state) {
          const newUrl = new URL(window.location.href);
          this.renderCollectionPage(newUrl.searchParams, false);
        }
      });
    },

    updateScroll: function (animate) {
      var scrollToElement = document.querySelector('[data-scroll-to]');

      if (!scrollToElement) {
        return;
      }

      var scrollTo = scrollToElement.getBoundingClientRect().top + window.pageYOffset;

      if (!theme.config.bpSmall) {
        scrollTo -= 15;
      }

      if (theme.config.stickyHeader) {
        var headerHeight = document.querySelector('.site-header').offsetHeight;
        scrollTo = scrollTo - headerHeight;
      }

      if (animate) {
        window.scrollTo({ top: scrollTo, behavior: 'smooth' });
      } else {
        window.scrollTo({ top: scrollTo });
      }
    },

    setFilterStickyPosition: function () {
      var header = document.querySelector('.site-header');
      var filters = document.querySelector(selectors.filters);

      if (!header || !filters) {
        return;
      }

      var headerHeight = header.offsetHeight;
      filters.style.top = headerHeight + 10 + 'px';

      var stickySidebar = document.querySelector('.grid__item--sidebar');
      if (stickySidebar) {
        stickySidebar.style.top = headerHeight + 10 + 'px';
      }
    },

    forceReload: function () {
      this.init();
    },

    startLoading: function () {
      var grid = document.querySelector(selectors.collectionGrid);

      if (grid) {
        grid.classList.add('unload');
      }
    },

    onUnload: function () {
      window.removeEventListener('resize', this.setFilterStickyPosition);
    }
  });

  return CollectionGrid;
})();

document.addEventListener('DOMContentLoaded', function () {
  var tries = 0;

  (function hookUp() {
    if (window.sections && window.sections.register) {
      window.sections.register('collection-grid', theme.CollectionGrid);
      return;
    }

    if (tries < 60) {
      tries += 1;
      setTimeout(hookUp, 50);
    }
  })();
});
